/**
 * Audit Store
 * Pinia store for audit state management
 */
import { defineStore } from 'pinia';
import { ref } from 'vue';
console.log('🔄 AUDIT STORE MODULE LOADED - VERSION 2');
const getExpectedAnimalScanTokens = (animal) => [animal.rapidTagBarcode, animal.rfidTagId].filter((token) => Boolean(token));
export const useAuditStore = defineStore('audit', () => {
    // State
    const currentLookup = ref(null);
    const expectedAnimals = ref([]);
    const cageInfo = ref(null);
    const scannedAnimals = ref([]);
    const isLoading = ref(false);
    const error = ref(null);
    const sseConnected = ref(false);
    const lookupProgressStatus = ref('idle');
    const lookupProgressMessage = ref(null);
    // Actions
    function setLookupCompleted(data) {
        console.log('🔵 setLookupCompleted CALLED', {
            scannedBarcode: data.rapidTagBarcode,
            cageId: data.cageId,
            expectedAnimalsCount: data.expectedAnimals.length,
            expectedScanTokens: data.expectedAnimals.flatMap(getExpectedAnimalScanTokens),
        });
        // Handle VMS format: cageId and cageNumber as direct properties
        const cageId = data.cageId || data.cage?.id;
        const cageNumber = data.cageNumber || data.cage?.number || '';
        currentLookup.value = {
            rapidTagBarcode: data.rapidTagBarcode,
            cageId: cageId,
            cage: cageId && cageNumber ? { id: cageId, number: cageNumber } : undefined,
            expectedAnimals: data.expectedAnimals.map((animal) => ({
                id: animal.id,
                rapidTagBarcode: animal.rapidTagBarcode,
                rfidTagId: animal.rfidTagId,
                rapidTagColour: animal.rapidTagColour,
                color: animal.color ||
                    animal.rapidTagColour,
            })),
            correlationId: data.correlationId,
            requestId: data.requestId,
            timestamp: data.timestamp,
        };
        cageInfo.value =
            cageId && cageNumber
                ? {
                    id: cageId,
                    number: cageNumber,
                }
                : cageId
                    ? {
                        id: cageId,
                        number: '', // VMS doesn't provide cage number
                    }
                    : null;
        expectedAnimals.value = data.expectedAnimals.map((animal) => ({
            id: animal.id,
            rapidTagBarcode: animal.rapidTagBarcode,
            rfidTagId: animal.rfidTagId,
            rapidTagColour: animal.rapidTagColour,
            color: animal.color ||
                animal.rapidTagColour,
            // Enhanced fields from VMS
            studyNumber: animal.studyNumber,
            studyGroup: animal.studyGroup,
            primeAnimalId: animal.primeAnimalId,
            homeCageNumber: animal.homeCageNumber?.toString(),
            location: animal.location,
        }));
        // Add the initially scanned animal to scannedAnimals list
        const scannedBarcode = data.rapidTagBarcode || data.scannedRapidTagBarcode;
        if (!scannedBarcode) {
            console.warn('⚠️ No rapidTagBarcode in event data, skipping initial scan');
            return;
        }
        console.log('🔍 Looking for scanned animal', {
            scannedBarcode,
            expectedAnimalsLength: expectedAnimals.value.length,
            expectedScanTokens: expectedAnimals.value.flatMap(getExpectedAnimalScanTokens),
        });
        const expectedAnimal = expectedAnimals.value.find((animal) => animal.rapidTagBarcode === scannedBarcode ||
            animal.rfidTagId?.trim().toUpperCase() === scannedBarcode.trim().toUpperCase());
        if (expectedAnimal) {
            // Add the first scanned animal (it's expected and was used to start the audit)
            console.log('✅ Adding initial scanned animal to list', {
                barcode: scannedBarcode,
                animalId: expectedAnimal.id,
                isExpected: true,
            });
            addScannedAnimal(scannedBarcode, true, expectedAnimal.id);
        }
        else {
            console.warn('⚠️ Initial scanned animal NOT FOUND in expected animals', {
                scannedBarcode,
                expectedScanTokens: expectedAnimals.value.flatMap(getExpectedAnimalScanTokens),
            });
        }
        error.value = null;
    }
    function setLoading(loading) {
        isLoading.value = loading;
    }
    function setLookupProgress(status, message) {
        lookupProgressStatus.value = status;
        lookupProgressMessage.value =
            message ??
                {
                    idle: null,
                    'publishing-scan': 'Sending scan request...',
                    'scan-accepted': 'Scan accepted. Looking up cage and animal details...',
                    'lookup-in-progress': 'Looking up cage and animal details...',
                    'loading-details': 'Loading animal details...',
                }[status];
    }
    function setError(err) {
        error.value = err;
        if (err) {
            setLookupProgress('idle');
        }
    }
    function setSSEConnected(connected) {
        sseConnected.value = connected;
    }
    function clearLookup() {
        currentLookup.value = null;
        expectedAnimals.value = [];
        cageInfo.value = null;
        error.value = null;
        setLookupProgress('idle');
    }
    function addScannedAnimal(barcode, isExpected, animalId) {
        // Check for duplicates
        const normalizedBarcode = barcode.trim().toUpperCase();
        const alreadyScanned = scannedAnimals.value.some((animal) => animal.rapidTagBarcode.trim().toUpperCase() === normalizedBarcode ||
            (isExpected && animalId && animal.isExpected && animal.animalId === animalId));
        if (alreadyScanned) {
            console.info('Animal already scanned, skipping', { barcode, animalId });
            return;
        }
        console.log('➕ Adding scanned animal', { barcode, isExpected, animalId });
        scannedAnimals.value.push({
            rapidTagBarcode: normalizedBarcode,
            scannedAt: new Date().toISOString(),
            isExpected,
            animalId,
        });
        console.log('📋 Scanned animals list updated', {
            count: scannedAnimals.value.length,
            barcodes: scannedAnimals.value.map((a) => a.rapidTagBarcode),
        });
    }
    function isAnimalScanned(barcode) {
        return scannedAnimals.value.some((animal) => animal.rapidTagBarcode === barcode);
    }
    function getScannedCount() {
        return scannedAnimals.value.length;
    }
    function getExpectedScannedCount() {
        return scannedAnimals.value.filter((animal) => animal.isExpected).length;
    }
    function reset() {
        clearLookup();
        scannedAnimals.value = [];
        isLoading.value = false;
        sseConnected.value = false;
        setLookupProgress('idle');
    }
    return {
        // State
        currentLookup,
        expectedAnimals,
        cageInfo,
        scannedAnimals,
        isLoading,
        error,
        sseConnected,
        lookupProgressStatus,
        lookupProgressMessage,
        // Actions
        setLookupCompleted,
        setLoading,
        setLookupProgress,
        setError,
        setSSEConnected,
        clearLookup,
        addScannedAnimal,
        isAnimalScanned,
        getScannedCount,
        getExpectedScannedCount,
        reset,
    };
});
//# sourceMappingURL=audit.store.js.map